import Bootpay from '@bootpay/client-js'
import type { RequestAuthenticationModel } from '@bootpay/client-js'
import { isApp } from './platform'

/**
 * 부트페이 본인인증 유틸리티 (API v2)
 * - 앱(WebView): Flutter 네이티브 SDK 사용 (handleBootpayVerificationNative)
 * - 웹 브라우저: JavaScript SDK 사용 (handleBootpayVerificationWeb)
 *
 * 본인인증 사용처 (모두 이 handleBootpayVerification 경로로 연동됨):
 * - 회원가입: Register.tsx → verifyPhoneForRegister() → handleBootpayVerification
 * - 비밀번호 찾기: PasswordFind.tsx → verifyPhoneForPasswordFind() → handleBootpayVerification
 * - 본인인증 테스트: PhoneVerificationTest.tsx → handleBootpayVerification
 */

/**
 * 부트페이 SDK 초기화 (웹 환경 전용)
 */
const initBootpaySDK = async (): Promise<void> => {
  const bootpayJavaScriptKey = process.env.REACT_APP_BOOTPAY_JAVASCRIPT_KEY

  console.log('[BootPay] SDK 초기화 시작')
  console.log(
    '[BootPay] BOOTPAY_JAVASCRIPT_KEY:',
    bootpayJavaScriptKey ? '설정됨' : '설정 안됨'
  )

  if (!bootpayJavaScriptKey) {
    console.error('[BootPay] JavaScript Key가 설정되지 않았습니다.')
    throw new Error('부트페이 설정이 완료되지 않았습니다.')
  }

  try {
    // BootPay API v2 초기화 - application_id 설정
    Bootpay.setApplicationId(bootpayJavaScriptKey)

    console.log('[BootPay] SDK 초기화 성공')
  } catch (error: any) {
    console.error('[BootPay] SDK 초기화 실패:', error)
    throw new Error('부트페이 SDK를 초기화할 수 없습니다.')
  }
}

/**
 * 부트페이 본인인증 실행
 */
export const handleBootpayVerification = async (
  onSuccess: (phone: string) => Promise<void>,
  onError: (error: string) => void,
  onCancel: () => void
): Promise<void> => {
  try {
    console.log('[BootPay] 본인인증 시작')

    // 앱 환경에서는 Flutter 네이티브 SDK 사용
    if (isApp() && (window as any).FlutterBridge) {
      console.log('[BootPay] Flutter 네이티브 SDK 사용')
      await handleBootpayVerificationNative(onSuccess, onError, onCancel)
      return
    }

    // 웹 환경에서는 JavaScript SDK 사용
    console.log('[BootPay] JavaScript SDK 사용')
    await handleBootpayVerificationWeb(onSuccess, onError, onCancel)
  } catch (error: any) {
    const errorMessage = error.message || '본인인증 중 오류가 발생했습니다.'
    onError(errorMessage)
  }
}

/**
 * Flutter 네이티브 SDK를 통한 본인인증
 */
const handleBootpayVerificationNative = async (
  onSuccess: (phone: string) => Promise<void>,
  onError: (error: string) => void,
  onCancel: () => void
): Promise<void> => {
  return new Promise((resolve) => {
    const flutterBridge = (window as any).FlutterBridge

    // 이벤트 리스너로 결과 받기
    const handleResult = (event: CustomEvent) => {
      const data = event.detail
      console.log('[BootPay] 네이티브 본인인증 결과:', data)

      // 이벤트 리스너 제거
      window.removeEventListener(
        'flutterBootpayVerificationResult',
        handleResult as EventListener
      )

      if (data.success && data.receiptId) {
        // receipt_id를 받아서 서버 검증 API 호출
        verifyPhoneWithReceiptId(data.receiptId, onSuccess, onError)
          .then(() => resolve())
          .catch(() => resolve())
      } else if (data.error) {
        // 취소 또는 에러 처리
        if (data.error.includes('취소')) {
          onCancel()
        } else {
          onError(data.error)
        }
        resolve()
      } else {
        onError('본인인증 결과를 받을 수 없습니다.')
        resolve()
      }
    }

    // 이벤트 리스너 등록
    window.addEventListener(
      'flutterBootpayVerificationResult',
      handleResult as EventListener
    )

    // 콜백 방식도 지원 (하위 호환성)
    flutterBridge.onBootpayVerificationResult = (data: any) => {
      console.log('[BootPay] 네이티브 본인인증 결과 (콜백):', data)
      const event = new CustomEvent('flutterBootpayVerificationResult', {
        detail: data,
      })
      window.dispatchEvent(event)
    }

    // Flutter 네이티브 SDK 호출
    if (flutterBridge.requestBootpayVerification) {
      flutterBridge.requestBootpayVerification()
    } else {
      onError('본인인증 기능을 사용할 수 없습니다.')
      resolve()
    }
  })
}

/**
 * JavaScript SDK를 통한 본인인증 (웹 환경)
 */
const handleBootpayVerificationWeb = async (
  onSuccess: (phone: string) => Promise<void>,
  onError: (error: string) => void,
  onCancel: () => void
): Promise<void> => {
  const bootpayJavaScriptKey = process.env.REACT_APP_BOOTPAY_JAVASCRIPT_KEY

  if (!bootpayJavaScriptKey) {
    console.error('[BootPay] JavaScript Key가 설정되지 않았습니다.')
    onError('본인인증 서비스 설정 오류가 발생했습니다.')
    return
  }

  // SDK 초기화
  await initBootpaySDK()

  const authenticationId = `VERIFY_${Date.now()}`
  console.log('[BootPay] 본인인증 ID:', authenticationId)

  // 부트페이 본인인증 요청 데이터 (API v2)
  // 참고: https://docs.bootpay.co.kr/?front=web&backend=go#payment-open-type
  // AUTH_CONFIRM_READY_FAILED 에러 방지를 위해 필수 파라미터만 포함
  const requestData: RequestAuthenticationModel = {
    application_id: bootpayJavaScriptKey,
    pg: 'kcp',
    order_name: '본인인증',
    authentication_id: authenticationId,
    // method 파라미터 제거 (본인인증은 기본값 사용)
    extra: {
      open_type: 'iframe', // iframe 모드 사용
    },
  }

  console.log('[BootPay] 요청 데이터:', requestData)
  console.log('[BootPay] requestAuthentication 메서드 호출 시작')

  try {
    // BootPay API v2는 Promise를 반환하며, 에러가 발생하면 에러 이벤트 객체를 throw합니다
    const response = await Bootpay.requestAuthentication(requestData)

      console.log('[BootPay] 응답 전체:', response)
      console.log('[BootPay] 응답 이벤트:', response.event)
      console.log('[BootPay] 응답 데이터:', response.data)

      // 응답이 에러인 경우
      if (response.event === 'error') {
        console.error('[BootPay] 본인인증 에러 코드:', response.error_code)
        console.error(
          '[BootPay] PG 에러 코드:',
          (response as any).pg_error_code
        )
        console.error('[BootPay] 본인인증 에러 메시지:', response.message)

        // AUTH_CONFIRM_READY_FAILED 에러 처리
        if (response.error_code === 'AUTH_CONFIRM_READY_FAILED') {
          const pgErrorCode = (response as any).pg_error_code
          let errorMessage =
            '본인인증 초기화에 실패했습니다. 부트페이 관리자 페이지에서 다음 사항을 확인해주세요:\n\n'
          errorMessage += '1. 본인인증 서비스 활성화 여부 확인\n'
          errorMessage += '2. KCP PG 설정 확인 (결제수단 설정 > KCP)\n'
          errorMessage +=
            '3. application_id가 본인인증 서비스에 연결되어 있는지 확인\n'
          errorMessage += '4. 도메인 화이트리스트 설정 확인\n\n'

          if (pgErrorCode === '9999') {
            errorMessage +=
              'KCP 측에서 사이트 설정 오류([CT01])가 발생했습니다. 부트페이 관리자 페이지의 KCP PG 설정을 확인하거나 부트페이 고객지원에 문의해주세요.'
          }

          onError(errorMessage)
          return
        }

        // RC_PM_NOT_FOUND 에러의 경우 부트페이 관리자 페이지에서 본인인증 서비스 활성화 필요
        if (response.error_code === 'RC_PM_NOT_FOUND') {
          onError(
            '본인인증 서비스가 설정되지 않았습니다. 부트페이 관리자 페이지 > 결제수단 설정에서 "본인인증" 서비스를 활성화해주세요. (결제 수단과는 별도로 본인인증 서비스를 활성화해야 합니다)'
          )
          return
        }

        const errorMessage =
          response.message || '본인인증 중 오류가 발생했습니다.'
        onError(errorMessage)
        return
      }

      // 취소된 경우
      if (response.event === 'cancel') {
        console.log('[BootPay] 본인인증 취소')
        onCancel()
        return
      }

      // 완료된 경우
      if (response.event === 'done') {
        try {
          // 응답에서 receipt_id 추출
          const receiptId =
            response.data?.receipt_id ||
            response.data?.receiptId ||
            response.receipt_id ||
            response.receiptId

          console.log('[BootPay] 영수증 ID:', receiptId)

          if (!receiptId) {
            throw new Error('본인인증 영수증 ID를 받을 수 없습니다.')
          }

          // receipt_id를 받아서 서버 검증 API 호출
          await verifyPhoneWithReceiptId(receiptId, onSuccess, onError)
        } catch (error: any) {
          const errorMessage =
            error.message || '본인인증 처리 중 오류가 발생했습니다.'
          onError(errorMessage)
        }
        return
      }

      // 알 수 없는 이벤트
      console.warn('[BootPay] 알 수 없는 이벤트:', response.event)
      const errorMessage =
        response.message || '본인인증 중 오류가 발생했습니다.'
      onError(errorMessage)
    } catch (e: any) {
      // BootPay는 에러가 발생하면 에러 이벤트 객체를 throw합니다
      console.error('[BootPay] 본인인증 예외 발생:', e)
      console.error('[BootPay] 에러 객체:', JSON.stringify(e, null, 2))

      // 에러 객체에 event 속성이 있는 경우 (에러 이벤트 객체)
      if (e && typeof e === 'object' && e.event) {
        switch (e.event) {
          case 'cancel':
            console.log('[BootPay] 본인인증 취소')
            onCancel()
            return
          case 'error':
            console.error('[BootPay] 본인인증 에러 코드:', e.error_code)
            console.error('[BootPay] PG 에러 코드:', e.pg_error_code)
            console.error('[BootPay] 본인인증 에러 메시지:', e.message)

            if (e.error_code === 'AUTH_CONFIRM_READY_FAILED') {
              const pgErrorCode = e.pg_error_code
              let errorMessage =
                '본인인증 초기화에 실패했습니다. 부트페이 관리자 페이지에서 다음 사항을 확인해주세요:\n\n'
              errorMessage += '1. 본인인증 서비스 활성화 여부 확인\n'
              errorMessage += '2. KCP PG 설정 확인 (결제수단 설정 > KCP)\n'
              errorMessage +=
                '3. application_id가 본인인증 서비스에 연결되어 있는지 확인\n'
              errorMessage += '4. 도메인 화이트리스트 설정 확인\n\n'

              if (pgErrorCode === '9999') {
                errorMessage +=
                  'KCP 측에서 사이트 설정 오류([CT01])가 발생했습니다. 부트페이 관리자 페이지의 KCP PG 설정을 확인하거나 부트페이 고객지원에 문의해주세요.'
              }

              onError(errorMessage)
            } else if (e.error_code === 'RC_PM_NOT_FOUND') {
              onError(
                '본인인증 서비스가 설정되지 않았습니다. 부트페이 관리자 페이지 > 결제수단 설정에서 "본인인증" 서비스를 활성화해주세요. (결제 수단과는 별도로 본인인증 서비스를 활성화해야 합니다)'
              )
            } else {
              const errorMessage =
                e.message || '본인인증 중 오류가 발생했습니다.'
              onError(errorMessage)
            }
            return
          default:
            const errorMessage = e.message || '본인인증 중 오류가 발생했습니다.'
            onError(errorMessage)
            return
        }
      } else {
        // 일반 에러인 경우
        const errorMessage = e?.message || '본인인증 중 오류가 발생했습니다.'
        onError(errorMessage)
      }
    }
};

/**
 * receipt_id를 사용하여 서버에서 전화번호 검증
 */
const verifyPhoneWithReceiptId = async (
  receiptId: string,
  onSuccess: (phone: string) => Promise<void>,
  onError: (error: string) => void
): Promise<void> => {
  try {
    console.log('[BootPay] 서버 검증 시작, receipt_id:', receiptId)

    const { authApi } = await import('../api/auth')
    const verifyResponse = await authApi.verifyPhone({
      receipt_id: receiptId,
    })

    if (!verifyResponse.verified) {
      throw new Error(
        verifyResponse.message || '본인인증에 실패했습니다.'
      )
    }

    if (!verifyResponse.phone) {
      throw new Error('전화번호 정보를 가져올 수 없습니다.')
    }

    console.log('[BootPay] 서버 검증 완료, 전화번호:', verifyResponse.phone)
    await onSuccess(verifyResponse.phone)
  } catch (error: any) {
    const errorMessage =
      error.message || '본인인증 처리 중 오류가 발생했습니다.'
    onError(errorMessage)
  }
}
